import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Searchbar } from '@/components/shared/Searchbar/Searchbar';
import { useERSContext } from '@/context';
import dayjs from 'dayjs';
import { constants } from '@/lib/constants';
import { useTranslation } from 'react-i18next';
import { useAIProvider } from '@/hooks/useAIProvider';
import { cn } from '@/lib/utils';
import { SearchResultSkeleton } from '@/components/shared/loader';

const SearchChat = ({ selectedChat, onSearchResultClick }) => {
  const { t, i18n } = useTranslation();
  const { chatHistory } = useERSContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [translations, setTranslations] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  const { callAPI } = useAIProvider({
    model: 'gpt-4o-mini',
    temperature: 0.3,
  });

  const chatData = chatHistory[selectedChat.id];
  const messages = chatData?.messages || [];

  // Batch translation for search results
  useEffect(() => {
    translateSearchResults();
  }, [searchResults, i18n.language]);

  const translateSearchResults = async () => {
    if (!searchResults || searchResults.length === 0) {
      setIsTranslating(false);
      return;
    }

    const translationItems = [];
    const textToTranslateMap = new Map();

    searchResults.forEach(message => {
      if (message.body && message.body.trim()) {
        const cacheKey = `search-${message.id}-${message.body}-${i18n.language}`;
        if (!translations[cacheKey] && !textToTranslateMap.has(message.body)) {
          textToTranslateMap.set(message.body, cacheKey);
          translationItems.push({
            text: message.body,
            cacheKey: cacheKey,
          });
        }
      }
    });

    // If no translations needed, skip API call
    if (translationItems.length === 0) {
      setIsTranslating(false);
      return;
    }

    // Single API call for all translations
    setIsTranslating(true);
    try {
      const texts = translationItems.map(item => item.text);
      const prompt = `Translate the following texts into ${i18n.language} without adding any extra context or punctuation. Provide only the translations in the same order, separated by "|||":\n\n${texts.map(text => text).join('\n')}`;

      const translated = await callAPI([{ role: 'user', content: prompt }]);

      const translationsArray = translated.split('|||').map(t => {
        let cleaned = t.trim();
        // Remove surrounding quotes
        if ((cleaned.startsWith('"') && cleaned.endsWith('"')) || (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
          cleaned = cleaned.slice(1, -1);
        }
        return cleaned.replace(/^["']+|["']+$/g, '').trim();
      });

      const newTranslations = {};
      translationItems.forEach((item, index) => {
        if (translationsArray[index]) {
          newTranslations[item.cacheKey] = translationsArray[index];
        }
      });

      setTranslations(prev => ({
        ...prev,
        ...newTranslations,
      }));
    } catch (err) {
      console.error('Search results batch translation failed:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  // Check if a specific search result has translations ready
  const isMessageTranslated = message => {
    if (!message.body) return true;

    const cacheKey = `search-${message.id}-${message.body}-${i18n.language}`;
    return translations[cacheKey] !== undefined;
  };

  // Get messages that need translation (for skeleton display)
  const getUntranslatedMessages = useMemo(() => {
    if (!isTranslating) return new Set();

    const untranslated = new Set();
    searchResults.forEach(message => {
      if (message.body && message.body.trim() && !isMessageTranslated(message)) {
        untranslated.add(message.id);
      }
    });
    return untranslated;
  }, [searchResults, isTranslating, translations, i18n.language]);

  // Helper function to get translated text
  const getTranslatedMessage = message => {
    if (!message.body) return '';
    const cacheKey = `search-${message.id}-${message.body}-${i18n.language}`;
    return translations[cacheKey] || message.body;
  };

  // Debounced function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  const performSearch = useCallback(
    term => {
      if (!term.trim()) {
        setSearchResults([]);
        return;
      }

      const lowercasedTerm = term.toLowerCase();
      const results = messages.filter(message => {
        if (!message.body) return false;

        const originalMatch = message.body.toLowerCase().includes(lowercasedTerm);

        const translatedText = translations[`search-${message.id}-${message.body}-${i18n.language}`];
        const translatedMatch = translatedText && translatedText.toLowerCase().includes(lowercasedTerm);

        return originalMatch || translatedMatch;
      });

      setSearchResults(results);
    },
    [messages, translations, i18n.language]
  );

  // Debounced search handler
  const debouncedSearch = useMemo(() => debounce(performSearch, 300), [performSearch]);

  // Handle search input change
  const handleSearchChange = value => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const formatDate = dateString => {
    return dayjs(dateString).format('hh:mm a');
  };

  // Function to highlight search text in message
  const highlightText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="text-interactive">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // Show skeletons if translating and no results are fully translated yet
  const showSkeletons =
    isTranslating && searchResults.length > 0 && !searchResults.some(message => isMessageTranslated(message));

  return (
    <div className="flex flex-col w-full h-full">
      {/* Fixed search bar alignment */}
      <div className="flex items-center justify-center p-2 h-20 w-full border-b border-b-border">
        <div className="w-full">
          <Searchbar
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t('CHAT_PANEL.SEARCH_PLACEHOLDER')}
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {searchTerm ? (
          // Search results view - scrollable with flex
          <div className="flex flex-col flex-1 overflow-hidden">
            {showSkeletons ? (
              // Show skeleton loaders while translating
              Array.from({ length: Math.min(5, searchResults.length) }).map((_, index) => (
                <SearchResultSkeleton key={`skeleton-${index}`} />
              ))
            ) : searchResults.length > 0 ? (
              <div className="flex flex-col overflow-y-auto flex-1">
                {searchResults.map(message => {
                  // Show skeleton only for messages that are being translated
                  const showSkeleton = isTranslating && getUntranslatedMessages.has(message.id);

                  return showSkeleton ? (
                    <SearchResultSkeleton key={`skeleton-${message.id}`} />
                  ) : (
                    <div
                      key={message.id}
                      onClick={() => onSearchResultClick(message)}
                      className="p-4 border-b cursor-pointer hover:bg-subtle transition-colors"
                    >
                      <span className="text-xs font-normal text-muted">
                        {formatDate(message.sendingDate || message.time)}
                      </span>
                      <p className="text-sm font-normal text-muted mt-1">
                        {highlightText(getTranslatedMessage(message), searchTerm)}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center">
                <div className="text-center text-placeholder py-4">
                  {t('CHAT_PANEL.NO_MESSAGES_FOUND_FOR')} "{searchTerm}"
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchChat;
